import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, TrendingUp } from "lucide-react";
import { Reveal, SectionLabel, Stagger, StaggerItem, Highlight } from "@/components/motion";
import CTABanner from "@/components/CTABanner";
import { DETAILED_SERVICES, PROCESS_STEPS, ENGAGEMENT_MODELS } from "@/data/content";

const SERVICE_STATS = [
  "90% faster document processing for a logistics client",
  "11 years of legacy data migrated with zero downtime",
  "3× organic traffic growth in 6 months for a health brand",
  "38% lift in conversion after a full design-system rebuild",
  "212% growth in qualified pipeline from paid campaigns",
  "Page-one rankings for 40+ commercial keywords",
  "42% cloud cost reduction after architecture review",
  "26.5 hours saved per week on one automation pipeline",
  "96% CRM adoption within 60 days of rollout",
  "ROI recovered within one quarter on analytics program",
];

const ServiceVisual = ({ Icon, index, service }) => (
  <div className="relative rounded-3xl bg-white border border-slate-100 p-8 sm:p-10 overflow-hidden min-h-[300px] flex flex-col justify-between soft-shadow group" aria-hidden="true">
    <div className="absolute inset-0 dot-grid opacity-30 pointer-events-none" />
    <div className={`absolute w-48 h-48 rounded-full blur-3xl opacity-15 pointer-events-none ${index % 2 === 0 ? "-top-10 -right-10 bg-[#2563EB]" : "-bottom-10 -left-10 bg-[#09B2ED]"}`} />
    
    <div className="flex items-center justify-between relative z-10">
      <span className="w-14 h-14 rounded-2xl bg-[#CAF2F9]/50 border border-[#91E0F1]/40 flex items-center justify-center text-[#2563EB] transition-transform duration-300 group-hover:scale-105">
        <Icon size={26} />
      </span>
      <span className="font-mono-brand text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400 bg-slate-50 border border-slate-100 px-2.5 py-1 rounded-full">
        CAPABILITY 0{index + 1}
      </span>
    </div>

    <div className="space-y-2 relative z-10 mt-8">
      <div className="h-1.5 w-1/3 bg-gradient-to-r from-[#2563EB] to-[#09B2ED] rounded-full" />
      <p className="text-xs font-mono-brand text-slate-500 font-medium pt-1">
        Production standard · Full lifecycle
      </p>
    </div>
  </div>
);

export default function Services() {
  return (
    <div data-testid="services-page">
      {/* HERO */}
      <section className="relative pt-36 pb-20 px-5 sm:px-8 overflow-hidden" data-testid="services-hero-section">
        <div className="absolute inset-0 bg-gradient-to-b from-[#CAF2F9]/30 to-white" />
        <div className="absolute -top-24 left-1/4 w-96 h-96 rounded-full bg-[#91E0F1]/20 blur-3xl" />
        <div className="absolute inset-0 dot-grid opacity-40" />
        <div className="relative max-w-4xl mx-auto text-center">
          <Reveal>
            <SectionLabel>Our Services</SectionLabel>
            <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#01193D] leading-[1.1]">
              Full-spectrum capability, <Highlight delay={0.4}>one partner</Highlight>
            </h1>
            <p className="text-[#475569] text-base lg:text-lg leading-relaxed mt-6 max-w-2xl mx-auto">
              From AI automation to brand identity, everything below is delivered by one integrated team — so your strategy, design, and engineering never fall out of sync.
            </p>
          </Reveal>
        </div>
      </section>

      {/* SERVICES OVERVIEW GRID */}
      <section className="px-5 sm:px-8 pb-16" data-testid="services-overview-section">
        <div className="max-w-7xl mx-auto">
          <Stagger className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" stagger={0.05}>
            {DETAILED_SERVICES.map((s, i) => (
              <StaggerItem key={s.title}>
                <a
                  href={`#service-${i}`}
                  data-testid={`service-anchor-${i}`}
                  className="card-lift group flex flex-col h-full bg-white border border-slate-100 rounded-2xl p-5 soft-shadow"
                >
                  <span className="inline-flex w-10 h-10 rounded-xl bg-gradient-to-br from-[#CAF2F9] to-white border border-[#91E0F1]/50 items-center justify-center">
                    <s.icon size={18} className="text-[#2563EB]" />
                  </span>
                  <p className="font-display font-semibold text-sm text-[#01193D] mt-3 leading-snug flex-1">{s.title}</p>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#2563EB] mt-3 opacity-60 group-hover:opacity-100 transition-opacity">
                    Jump to <ArrowRight size={12} />
                  </span>
                </a>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* SERVICE BLOCKS */}
      <section className="px-5 sm:px-8 pb-8" data-testid="services-list-section">
        <div className="max-w-7xl mx-auto space-y-20 lg:space-y-28">
          {DETAILED_SERVICES.map((s, i) => (
            <Reveal key={s.title} id={`service-${i}`} className="scroll-mt-28">
              <div className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}>
                <div data-testid={`service-block-${i}`}>
                  <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase text-[#2563EB]">
                    <span className="w-8 h-[2px] bg-gradient-to-r from-[#2563EB] to-[#09B2ED]" /> Service {String(i + 1).padStart(2, "0")}
                  </span>
                  <h2 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-[#01193D] mt-4 leading-tight">
                    {s.title}
                  </h2>
                  <p className="text-[#475569] text-base leading-relaxed mt-5">{s.desc}</p>
                  <ul className="mt-7 space-y-3">
                    {s.capabilities.map((c) => (
                      <li key={c} className="flex items-start gap-3 text-sm text-[#01193D]">
                        <CheckCircle2 size={18} className="text-[#09B2ED] shrink-0 mt-0.5" />
                        {c}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6 inline-flex items-center gap-2.5 bg-[#CAF2F9]/60 border border-[#91E0F1]/40 rounded-2xl px-4 py-3">
                    <TrendingUp size={16} className="text-[#2563EB] shrink-0" />
                    <span className="text-xs font-semibold text-[#01193D]">{SERVICE_STATS[i]}</span>
                  </div>
                </div>
                <ServiceVisual Icon={s.icon} index={i} />
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* DELIVERY PROCESS */}
      <section className="px-5 sm:px-8 py-20 lg:py-28 mt-16 bg-[#01193D] relative overflow-hidden" data-testid="services-process-section">
        <div className="absolute inset-0 dot-grid-light opacity-30" />
        <div className="absolute -bottom-32 left-1/3 w-96 h-96 rounded-full bg-[#2563EB]/20 blur-3xl animate-blob" />
        <div className="max-w-7xl mx-auto relative">
          <Reveal className="text-center max-w-2xl mx-auto">
            <SectionLabel dark>How We Deliver</SectionLabel>
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-[44px] tracking-tight text-white">
              The same rigor, every service
            </h2>
            <p className="text-slate-300 mt-5">
              Whether it's a two-week sprint or a year-long build, every engagement follows our five-phase framework.
            </p>
          </Reveal>
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5 mt-14" stagger={0.12}>
            {PROCESS_STEPS.map((step) => (
              <StaggerItem key={step.num} className="bg-white/[0.06] border border-white/10 rounded-3xl p-7 backdrop-blur-sm hover:bg-white/10 transition-colors">
                <span className="font-display font-bold text-3xl text-[#09B2ED]">{step.num}</span>
                <h3 className="font-display font-semibold text-lg text-white mt-3">{step.title}</h3>
                <p className="text-slate-300 text-sm leading-relaxed mt-2">{step.desc}</p>
              </StaggerItem>
            ))}
          </Stagger>
          <Reveal className="text-center mt-14">
            <p className="text-slate-300 mb-6">Not sure what you need? That's exactly what the first conversation is for.</p>
            <Link to="/contact" data-testid="services-bottom-cta" className="btn-gradient inline-flex items-center gap-2 text-white font-semibold px-8 py-4 rounded-full">
              Let's Talk <ArrowRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ENGAGEMENT MODELS */}
      <section className="px-5 sm:px-8 py-16 lg:py-24" data-testid="engagement-models-section">
        <div className="max-w-7xl mx-auto">
          <Reveal className="text-center max-w-2xl mx-auto">
            <SectionLabel>Ways to Work With Us</SectionLabel>
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-[44px] tracking-tight text-[#01193D]">
              Pick the engagement model that fits
            </h2>
          </Reveal>
          <Stagger className="grid md:grid-cols-3 gap-6 mt-14">
            {ENGAGEMENT_MODELS.map((m) => (
              <StaggerItem key={m.title}>
                <div
                  data-testid={`engagement-model-${m.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`card-lift h-full flex flex-col rounded-3xl p-8 border ${
                    m.featured ? "bg-[#01193D] border-transparent relative overflow-hidden" : "bg-white border-slate-100 soft-shadow"
                  }`}
                >
                  {m.featured && <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-[#2563EB]/25 blur-2xl" />}
                  {m.featured && (
                    <span className="relative self-start text-[10px] font-bold tracking-[0.15em] uppercase bg-gradient-to-r from-[#2563EB] to-[#09B2ED] text-white px-3 py-1 rounded-full mb-4">
                      Most Popular
                    </span>
                  )}
                  <h3 className={`relative font-display font-semibold text-2xl ${m.featured ? "text-white" : "text-[#01193D]"}`}>{m.title}</h3>
                  <p className={`relative text-sm leading-relaxed mt-3 ${m.featured ? "text-slate-300" : "text-[#475569]"}`}>{m.desc}</p>
                  <ul className="relative space-y-3 mt-6 flex-1">
                    {m.features.map((f) => (
                      <li key={f} className={`flex items-start gap-2.5 text-sm ${m.featured ? "text-slate-200" : "text-[#01193D]"}`}>
                        <CheckCircle2 size={16} className="text-[#09B2ED] shrink-0 mt-0.5" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact"
                    data-testid={`engagement-cta-${m.title.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`relative mt-8 inline-flex items-center justify-center gap-2 font-semibold px-6 py-3.5 rounded-full text-sm ${
                      m.featured ? "btn-gradient text-white" : "btn-outline-navy"
                    }`}
                  >
                    {m.cta} <ArrowRight size={16} />
                  </Link>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <CTABanner title="Ready to put the right expertise behind your goals?" subtitle="One conversation is all it takes to find out what's possible." />
    </div>
  );
}
